'use client';

import clsx from 'clsx';
import { useQueryState } from 'next-usequerystate';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import reactStringReplace from 'react-string-replace';
import { StoryblokStory } from 'storyblok-generate-ts';
import { useDebounce } from 'use-debounce';

import { Icon, Link, LoadingSpinner, Typography } from '@/components/core';
import { useLanguageParam } from '@/hooks';
import { useOutsideClick } from '@/hooks/useClickOutside';
import { useDeviceDetector } from '@/hooks/useDeviceDetector';
import { MicrocopyStoryblok } from '@/types/storyblok/storyblok-types';
import { parseStoryblokUrl } from '@/utils/storyblok';
import { useSearchBarStore } from './searchBarStore';

import styles from './SearchBar.module.scss';

type SearchBarProps = {
    translations: MicrocopyStoryblok;
    className?: string;
};

export const SearchBar: React.FC<SearchBarProps> = ({ translations, className }) => {
    const language = useLanguageParam();
    const { isSearchBarOpen, setIsSearchBarOpen } = useSearchBarStore();
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useQueryState('searchTerm', { defaultValue: '', shallow: true });
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const [searchResults, setSearchResults] = useState<StoryblokStory<any>[]>([]);

    const reset = () => {
        setIsSearchBarOpen(false);
        setSearchTerm(null);
        setIsSearchBarOpen(false);
    };

    const ref = useOutsideClick(() => reset());
    const inputRef = useRef<HTMLInputElement>(null);
    const device = useDeviceDetector();

    const { searchPlaceholder, searchDescription, searchError } = translations;

    const fetchSearchResults = async (searchQuery: string) => {
        try {
            setIsLoading(true);

            const queryParams = new URLSearchParams({
                language,
                startsWith: language,
                searchTerm: searchQuery,
                sortBy: 'name:asc',
                excludingFields: 'body',
            });
            const data: StoryblokStory<any>[] = await fetch(`/api/stories/?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(response => response.json());
            const filtered = data.filter(story => story.full_slug?.split('/').length > 2);
            setSearchResults(filtered);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup function to clear results when searchbar opens and there is no search term but search result data is not empty
    useEffect(() => {
        if (isSearchBarOpen && !searchTerm) {
            setSearchResults([]);
        }
    }, [isSearchBarOpen, searchTerm]);

    useEffect(() => {
        if (!debouncedSearchTerm) {
            setSearchResults([]);
            return;
        }

        setIsSearchBarOpen(true);
        fetchSearchResults(debouncedSearchTerm);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);

    const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value) {
            setSearchTerm(null);
            return;
        }
        setSearchTerm(event.target.value);
    };
    function renderSearchResults() {
        if (isLoading) {
            return (
                <div className={styles.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            );
        }

        if (searchResults.length > 0) {
            return (
                <div className={styles.searchResultsData}>
                    {searchResults?.map(result => (
                        <Link
                            key={result.uuid}
                            href={parseStoryblokUrl(result.full_slug)}
                            className={styles.searchResultItem}
                        >
                            {reactStringReplace(
                                result?.content?.searchResultName || result.name,
                                debouncedSearchTerm,
                                (match, i) => (
                                    <span
                                        key={match + i}
                                        className="bold"
                                    >
                                        {match}
                                    </span>
                                ),
                            )}
                        </Link>
                    ))}
                </div>
            );
        }

        if (!!debouncedSearchTerm && searchResults?.length === 0) {
            return (
                <Typography>
                    {reactStringReplace(searchError, '{searchterm}', (match, i) => (
                        <span
                            key={match + i}
                            className={clsx('bold', styles.line)}
                        >
                            {debouncedSearchTerm}
                        </span>
                    ))}
                </Typography>
            );
        }

        return <Typography>{searchDescription}</Typography>;
    }
    return (
        <div
            ref={ref}
            className={clsx(
                styles.root,
                {
                    [styles.searchBarOpen]: isSearchBarOpen,
                    [styles.noResults]: searchResults.length === 0 && !isLoading && isSearchBarOpen,
                    bodyNoScroll: device !== 'Large' && isSearchBarOpen,
                },
                className,
            )}
        >
            <div className={styles.searchbarWrapper}>
                <div className={styles.wrapper}>
                    <div
                        className={styles.searchbar}
                        onClick={() => inputRef.current?.focus()}
                    >
                        <input
                            ref={inputRef}
                            className={styles.input}
                            type="text"
                            name="searchbar"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={onSearchChange}
                            onClick={() => {
                                setTimeout(() => {
                                    inputRef.current?.focus();
                                }, 10);
                            }}
                        />
                        {isSearchBarOpen ? (
                            <Icon
                                className={styles.closeIcon}
                                name="close"
                                onClick={e => {
                                    e.stopPropagation();
                                    setSearchTerm(null);
                                    setIsSearchBarOpen(false);
                                }}
                            />
                        ) : (
                            <Icon
                                name="search"
                                className={styles.icon}
                            />
                        )}
                    </div>
                </div>

                <div className={styles.searchResultsWrapper}>
                    <div className={styles.searchResults}>{renderSearchResults()}</div>
                </div>
            </div>
        </div>
    );
};
